import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Action, ActionStatus, FormField, StrictFormDefinition, VuetifySelectItem } from '@/application/types';
import { showErrorResponse, showSuccess } from '@/application/snackbar/service';
import { isTextFilled } from '@/helpers/form-helpers';
import { AddressFormLine, CorrectAddressesCommand, GetInvalidAddressQuery, InvalidAddress } from '../../types';

const addressCorrectionState = namespace('addressCorrection');

interface Form extends StrictFormDefinition {
  fields: {
    invalidAddresses: FormField<AddressFormLine[]>;
  }
}

@Component
export default class InvalidAddresses extends Vue {

  @addressCorrectionState.Action
  readonly getInvalidAddresses!: Action<void>;

  @addressCorrectionState.Action
  readonly getInvalidAddress!: Action<GetInvalidAddressQuery>;

  @addressCorrectionState.Action
  readonly updateInvalidAddressesLimit!: Action<number>;

  @addressCorrectionState.Action
  readonly correctAddresses!: Action<CorrectAddressesCommand>;

  @addressCorrectionState.State
  readonly invalidAddresses!: InvalidAddress[];

  @addressCorrectionState.State
  readonly limit!: number;

  @addressCorrectionState.State
  readonly getInvalidAddressesStatus!: ActionStatus;

  @addressCorrectionState.State
  readonly getInvalidAddressStatus!: ActionStatus;

  @addressCorrectionState.State
  readonly correctAddressesStatus!: ActionStatus;

  readonly limitOptions: VuetifySelectItem<number>[] = [
    { text: '10 Adressen', value: 10 },
    { text: '20 Adressen', value: 20 },
    { text: '50 Adressen', value: 50 },
  ];

  form: Form | null = null;

  get isLoading(): boolean {
    return this.getInvalidAddressesStatus === ActionStatus.InProgress
      || this.getInvalidAddressStatus === ActionStatus.InProgress;
  }

  get isSubmitAllowed(): boolean {
    return !this.isLoading && !this.isProcessing;
  }

  get isProcessing(): boolean {
    return this.correctAddressesStatus === ActionStatus.InProgress;
  }

  mounted(): void {
    if (this.$route.params.addressId) {
      const query: GetInvalidAddressQuery = {
        addressId: this.$route.params.addressId,
      };
      this.getInvalidAddress(query)
        .then(() => {
          this.form = this.buildForm();
        })
        .catch((error) => showErrorResponse(error));
    } else {
      this.getInvalidAddresses()
        .then(() => {
          this.form = this.buildForm();
        })
        .catch((error) => showErrorResponse(error));
    }
  }

  buildForm(): Form {
    const addressesForForm: AddressFormLine[] = this.invalidAddresses.map((invalidAddress) => ({
      addressId: invalidAddress.addressId,
      orderId: invalidAddress.orderId,
      orderNumber: invalidAddress.orderNumber,
      company: invalidAddress.company,
      name1: invalidAddress.name1,
      name2: invalidAddress.name2,
      name3: invalidAddress.name3,
      street: invalidAddress.street,
      houseNumber: invalidAddress.houseNumber,
      zip: invalidAddress.zip,
      city: invalidAddress.city,
      state: invalidAddress.state,
      country: invalidAddress.country,
      isSkipped: false,
    }));

    return {
      valid: false,
      fields: {
        invalidAddresses: {
          value: addressesForForm,
        },
      },
    };
  }

  formSubmitted(): void {
    const correctedAddresses = this.form!.fields.invalidAddresses.value
      .filter((address) => !address.isSkipped)
      .map((invalidAddress) => ({
        addressId: invalidAddress.addressId,
        orderId: invalidAddress.orderId,
        company: isTextFilled(invalidAddress.company) ? invalidAddress.company : undefined,
        name1: isTextFilled(invalidAddress.name1) ? invalidAddress.name1 : undefined,
        name2: isTextFilled(invalidAddress.name2) ? invalidAddress.name2 : undefined,
        name3: isTextFilled(invalidAddress.name3) ? invalidAddress.name3 : undefined,
        street: isTextFilled(invalidAddress.street) ? invalidAddress.street : undefined,
        houseNumber: isTextFilled(invalidAddress.houseNumber) ? invalidAddress.houseNumber : undefined,
        zip: isTextFilled(invalidAddress.zip) ? invalidAddress.zip : undefined,
        city: isTextFilled(invalidAddress.city) ? invalidAddress.city : undefined,
        state: isTextFilled(invalidAddress.state) ? invalidAddress.state : undefined,
        country: isTextFilled(invalidAddress.country) ? invalidAddress.country : undefined,
      }));

    const command: CorrectAddressesCommand = {
      addresses: correctedAddresses,
    };
    this.correctAddresses(command)
      .then(() => showSuccess({ message: 'Adressen wurden korrigiert' }))
      .catch((error) => showErrorResponse(error));
  }

  limitChanged(limit: number): void {
    if (limit === this.limit) {
      return;
    }

    this.updateInvalidAddressesLimit(limit)
      .then(() => {
        this.form = this.buildForm();
      })
      .catch((error) => showErrorResponse(error));
  }

}
